// src/hooks/useTokenRefresh.js
//
// Thin React adapter over the singleton TB token store (services/tbToken.js).
// The singleton — not this hook — is the single source of truth and owner of
// the token lifecycle. This hook only:
//   1. seeds the singleton from the /api/ref/validate result on mount,
//   2. schedules a PROACTIVE refresh ~5 min before the JWT's exp (so the token
//      is replaced before anything uses an expired one),
//   3. re-renders consumers when the singleton's token changes,
//   4. exposes forceRefresh (= singleton single-flight refresh) for any caller
//      that still wants an explicit refresh.
//
// Consumers should prefer `ensureFresh()` from services/tbToken at call time
// (it refreshes proactively and returns a guaranteed-valid token) rather than
// holding the returned `activeToken` as a long-lived copy.
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  initTbToken,
  getAccessToken,
  getExpMs,
  refresh as tbRefresh,
  subscribe as tbSubscribe,
} from '../services/tbToken';

export function useTokenRefresh(
  initialToken,
  initialRefreshToken,
  apiBase,
  fallbackIntervalHours = 2
) {
  // Seed the singleton as soon as we have validate output. Idempotent.
  if (initialToken || initialRefreshToken) {
    initTbToken({
      accessToken: initialToken,
      refreshToken: initialRefreshToken,
      apiBase,
    });
  }

  const [activeToken, setActiveToken] = useState(() => getAccessToken());
  const timerRef = useRef(null);

  // Re-render whenever the singleton swaps the token.
  useEffect(() => tbSubscribe((tok) => setActiveToken(tok)), []);

  // Proactive scheduler: refresh 5 min before exp (driven by the JWT's own
  // exp claim, not a wall-clock guess). Reschedules whenever the token changes.
  useEffect(() => {
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    clear();
    if (!activeToken) return clear;

    const REFRESH_BEFORE_EXP_MS = 5 * 60 * 1000;
    const MIN_DELAY_MS = 30 * 1000;
    const MAX_DELAY_MS = 6 * 60 * 60 * 1000;

    const expMs = getExpMs();
    let delay;
    if (expMs) {
      delay = Math.max(MIN_DELAY_MS, expMs - Date.now() - REFRESH_BEFORE_EXP_MS);
    } else {
      delay = Math.min(MAX_DELAY_MS, fallbackIntervalHours * 60 * 60 * 1000);
    }
    timerRef.current = setTimeout(() => tbRefresh(), delay);
    return clear;
  }, [activeToken, fallbackIntervalHours]);

  const forceRefresh = useCallback(() => tbRefresh(), []);

  return { activeToken, refreshToken: undefined, forceRefresh };
}
